import classes from './login.module.scss';
import { ILoginForm } from '../../Hooks/login.hook';
import { useLogin } from '../../Hooks';

import { Button, Checkbox, Form, Input, Row, Typography } from 'antd';
import { useParams } from 'react-router-dom';
import { Wrapper } from '../../components/shared/wrapper.component';
import { RiLockFill, RiUserFill } from 'react-icons/ri';
import { GoogleReCaptcha } from 'react-google-recaptcha-v3';
import useGoogleAuth from '../../Hooks/google-auth.hook';
import useFacebookAuth from '../../Hooks/facebook-auth.hook';
import { FacebookFilled } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

const LoginPage = () => {
  const { t } = useTranslation();
  const { locale = 'en' } = useParams<{ locale: string }>();
  const login = useLogin();
  const { googleSignInHandler, loading: googleLoading } = useGoogleAuth();
  const { facebookSignInHandler, loading: facebookLoading } = useFacebookAuth();

  return (
    <Wrapper>
      <div className={classes.loginContainer}>
        <div className={classes.textContainer}>
          <h1 className={classes.title}>{t('login.welcome-back')}</h1>
          <p className={classes.text}>{t('login.enter-details')}</p>
        </div>
        <Form<ILoginForm>
          className={classes.loginForm}
          initialValues={{ rememberMe: true }}
          onFinish={login.loginHandler}
          disabled={login.loading}
          validateTrigger="onBlur"
        >
          <Form.Item
            className={classes.formItem}
            name="username"
            rules={[
              { required: true, message: t('validation.email-required') },
              { type: 'email', message: t('validation.email-invalid') }
            ]}
          >
            <Input
              className={classes.inputField}
              prefix={<RiUserFill color="grey" />}
              placeholder={t('login.email')}
            />
          </Form.Item>
          <Form.Item
            className={classes.formItem}
            name="password"
            rules={[{ required: true, message: t('validation.password-required') }]}
          >
            <Input
              className={classes.inputField}
              prefix={<RiLockFill color="grey" />}
              type="password"
              placeholder={t('login.password')}
            />
          </Form.Item>
          <Form.Item className={classes.formItem}>
            <Row justify="space-between">
              <Form.Item
                className={classes.formItem}
                name="rememberMe"
                valuePropName="checked"
                noStyle
              >
                <Checkbox>{t('login.remember-me')}</Checkbox>
              </Form.Item>
              <Typography.Link className="login-form-forgot" href={`/${locale}/forgot-password`}>
                {t('login.forgot-password')}
              </Typography.Link>
            </Row>
          </Form.Item>
          <Form.Item className={classes.formItem}>
            <Button
              type="primary"
              htmlType="submit"
              className={classes.loginBtn}
              loading={login.loading}
            >
              {t('login.log-in')}
            </Button>
            <p className={classes.signUpText}>
              <span>
                {t('login.no-account')}&nbsp;
                <Typography.Link href={`/${locale}/register?${new URLSearchParams({ dest: login.dest }).toString()}`}>{t('login.sign-up')}</Typography.Link>
              </span>
            </p>
          </Form.Item>
          <Form.Item className={classes.formItem}>
            <Typography.Text className={classes.orText}>{t('common.or')}</Typography.Text>
            <Button
              className={classes.googleBtn}
              loading={googleLoading}
              onClick={googleSignInHandler}
              icon={<img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt='google_image' width={18} height={18} />}
            >
              {t('login.continue-google')}
            </Button>
            <Button
              className={classes.facebookBtn}
              loading={facebookLoading}
              onClick={facebookSignInHandler}
              icon={<FacebookFilled style={{ color: '#1877F2', fontSize: 20 }} />}
            >
              {t('login.continue-facebook')}
            </Button>
          </Form.Item>
          <GoogleReCaptcha
            onVerify={login.captchaVerifyHandler}
            refreshReCaptcha={login.refreshReCaptcha}
          />
        </Form>
        {login?.email && <div className={classes.resendContainer}>{t('login.email-not-received')}
          <Button
            type='link'
            disabled={!!login.resendTimer || login.loading}
            onClick={() => !login.resendTimer && login?.email && login.resendEmailVerificationHandler(login.email)}
          >
            &nbsp;{t('login.request-again')}&nbsp;
          </Button>
          {!!login.resendTimer && ` ${t('login.in-seconds', { seconds: login.resendTimer })}`}
        </div>}
      </div>
    </Wrapper>
  );
};

export default LoginPage;